import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Don't redirect for silent auth checks (favorites, etc.)
    const silentPaths = ['/favorites/check', '/favorites'];
    // Kein Login-Redirect: Endpunkte, die auch als Gast funktionieren
    const noRedirectPaths = ['/hotel-inquiries'];
    const isSilent = silentPaths.some(p => originalRequest.url?.includes(p));
    const noRedirect = noRedirectPaths.some(p => originalRequest.url?.includes(p));
    if (error.response?.status === 401 && !originalRequest._retry && !isSilent) {
      originalRequest._retry = true;
      try {
        await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
        return api(originalRequest);
      } catch {
        if (!noRedirect) window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Calculator - new multi-vehicle format
export const calculateSavings = (data) => api.post('/calculator/savings', data);
export const quickCalculateSavings = (data) => api.post('/calculator/quick-savings', data);
export const getPackages = () => api.get('/calculator/packages');
export const calculatePackageSavings = (data) => api.post('/calculator/package-savings', data);

// Vehicles
export const getVehicles = () => api.get('/vehicles');
export const createVehicle = (data) => api.post('/vehicles', data);
export const deleteVehicle = (id) => api.delete(`/vehicles/${id}`);

// Orders - new order flow
export const createOrder = (data) => api.post('/orders/create', data);
export const createOrderCheckout = (orderId) => api.post(`/orders/checkout?order_id=${orderId}`);
export const getOrderStatus = (sessionId) => api.get(`/orders/status/${sessionId}`);

// Credits
export const getCredits = () => api.get('/credits');
export const getCreditsSummary = () => api.get('/credits/summary');

// Hotels
export const getHotels = (params) => api.get('/hotels', { params });
export const getHotel = (id) => api.get(`/hotels/${id}`);
export const getHotelDetail = (id, params) => api.get(`/hotels/detail/${id}`, { params });
export const autocompleteLocations = (text) => api.get(`/hotels/autocomplete?text=${encodeURIComponent(text)}`);

// Bookings
export const getBookings = () => api.get('/bookings');
export const createBooking = (data) => api.post('/bookings', data);
export const cancelBooking = (id) => api.delete(`/bookings/${id}`);

// Admin
export const getAdminUsers = () => api.get('/admin/users');
export const createAdminUser = (data) => api.post('/admin/users', data);
export const updateAdminUser = (id, data) => api.put(`/admin/users/${id}`, data);
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminEmailLog = (params) => api.get('/admin/email-log', { params });
export const postAdminCleanup = (payload) => api.post('/admin/cleanup-data', payload);
export const refreshHotelPhotos = () => api.post('/admin/hotels/refresh-photos');
export const getHotelPhotosStatus = () => api.get('/admin/hotels/refresh-photos/status');
// Nearby Places (OSM Overpass) — Werkstätten/TÜV/Glasrep in Kundennähe
export const adminNearbyPlaces = ({ postal_code, service, city, radius_km = 15, limit = 12, min_rating = 0 }) =>
  api.get('/admin/nearby-places', { params: { postal_code, service, city, radius_km, limit, min_rating } });
// Google Place-Details inkl. Reviews (On-Demand — spart API-Credits)
export const adminPlaceDetails = (place_id, max_reviews = 3) =>
  api.get('/admin/place-details', { params: { place_id, max_reviews } });

// CRM: Fahrzeuge / Versicherungen / Haushalt bearbeiten (Admin editiert im Namen des Kunden)
export const crmUpdateVehicle    = (uid, vid, data) => api.patch(`/crm/customers/${uid}/vehicles/${vid}`, data);
export const crmDeleteVehicle    = (uid, vid)       => api.delete(`/crm/customers/${uid}/vehicles/${vid}`);
export const crmAddInsurance     = (uid, data)      => api.post(`/crm/customers/${uid}/insurances`, data);
export const crmUpdateInsurance  = (uid, iid, data) => api.patch(`/crm/customers/${uid}/insurances/${iid}`, data);
export const crmDeleteInsurance  = (uid, iid)       => api.delete(`/crm/customers/${uid}/insurances/${iid}`);
export const crmAddHousehold     = (uid, data)      => api.post(`/crm/customers/${uid}/household`, data);
export const crmUpdateHousehold  = (uid, mid, data) => api.patch(`/crm/customers/${uid}/household/${mid}`, data);
export const crmDeleteHousehold  = (uid, mid)       => api.delete(`/crm/customers/${uid}/household/${mid}`);
export const crmSendSubscriptionInvite = (uid, data) => api.post(`/crm/customers/${uid}/send-subscription-invite`, data);
export const crmAppointmentRequest     = (data)      => api.post('/crm/appointment-request', data);

// Hotel Inquiries
export const createHotelInquiry = (data) => api.post('/hotel-inquiries', data);
export const getHotelInquiries = () => api.get('/hotel-inquiries');
export const getMyInquiries = () => api.get('/hotel-inquiries/my');
export const processInquiry = (id, data) => api.put(`/hotel-inquiries/${id}/process`, data);

// Subscriptions
export const createSubscriptionCheckout = (data) => api.post('/subscriptions/checkout', data);
export const getSubscriptionStatus = (sessionId) => api.get(`/subscriptions/status/${sessionId}`);
export const getMySubscriptions = () => api.get('/subscriptions/my');
export const getGuestPurchases = () => api.get('/subscriptions/admin/guest-purchases');
export const getLuxusInquiries = () => api.get('/luxus-inquiries');
export const resendGuestPurchaseEmail = (subId) => api.post(`/subscriptions/admin/guest-purchases/${subId}/resend-email`);

// Credits
export const getCreditTransactions = (params) => api.get('/credits/transactions', { params });
export const exportCreditsCSV = () => api.get('/credits/export', { responseType: 'blob' });

// Admin
export const getAdminSubscriptions = () => api.get('/admin/subscriptions');
export const getAdminRevenue = () => api.get('/admin/revenue');

// Fraud
export const getFraudAlerts = () => api.get('/fraud/alerts');
export const resolveFraudAlert = (id) => api.put(`/fraud/alerts/${id}/resolve`);
export const dismissFraudAlert = (id) => api.put(`/fraud/alerts/${id}/dismiss`);
export const runFraudScan = () => api.post('/fraud/scan');

// Gas Stations
export const getEUFuelPrices = () => api.get('/gas-stations/eu-prices');
export const searchGasStations = (params) => api.get('/gas-stations/search', { params });
export const getMajorCities = () => api.get('/gas-stations/cities');

// Auth - Password Reset
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });
export const changePassword = (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password });

// Checkout - Discount Code
export const validateDiscountCode = (code) => api.post('/checkout/validate-code', { code });

// Team / Master Account
export const createCompany = (data) => api.post('/team/create', data);
export const getCompany = () => api.get('/team');
export const joinCompany = (invite_code) => api.post('/team/join', { invite_code });
export const removeMember = (memberId) => api.delete(`/team/members/${memberId}`);
export const regenerateInviteCode = () => api.post('/team/regenerate-code');
export const getTeamStats = () => api.get('/team/stats');

// Favorites
export const getFavorites = () => api.get('/favorites');
export const addFavorite = (data) => api.post('/favorites', data);
export const removeFavorite = (hotelId) => api.delete(`/favorites/${hotelId}`);
export const checkFavorite = (hotelId) => api.get(`/favorites/check/${hotelId}`);

// Wallet (FuelX Card + Tankgutschrift)
export const getWalletBalance = () => api.get('/wallet/balance');
export const getWalletTransactions = (params) => api.get('/wallet/transactions', { params });
export const redeemWalletCredit = (data) => api.post('/wallet/redeem', data);
export const issueWalletCard = () => api.post('/wallet/issue-card');
// Admin Wallet
export const adminListWallets = (limit = 200) => api.get(`/wallet/admin/all?limit=${limit}`);
export const adminWalletCredit = (userId, data) => api.post(`/wallet/admin/${userId}/credit`, data);
export const adminWalletDebit = (userId, data) => api.post(`/wallet/admin/${userId}/debit`, data);
export const adminWalletUpdateCard = (userId, data) => api.patch(`/wallet/admin/${userId}/card`, data);
export const adminUserTransactions = (userId, limit = 100) => api.get(`/wallet/admin/${userId}/transactions?limit=${limit}`);

// Profile (Stammdaten / Fahrzeuge / Versicherungen)
export const getMyProfile = () => api.get('/profile/me');
export const getOnboardingStatus = () => api.get('/profile/onboarding');
export const updateMyProfile = (data) => api.put('/profile/me', data);
export const listHouseholdMembers = () => api.get('/profile/household');
export const addHouseholdMember = (data) => api.post('/profile/household', data);
export const deleteHouseholdMember = (id) => api.delete(`/profile/household/${id}`);
export const listMyVehicles = () => api.get('/profile/vehicles');
export const hsnLookup = (hsn) => api.get('/profile/vehicles/hsn-lookup', { params: { hsn } });
export const getVehicleFile = (id) => api.get(`/profile/vehicles/${id}/file`);
export const getMyDeadlines = () => api.get('/profile/deadlines');
export const createProfileVehicle = (data) => api.post('/profile/vehicles', data);
export const updateProfileVehicle = (id, data) => api.patch(`/profile/vehicles/${id}`, data);
export const deleteProfileVehicle = (id) => api.delete(`/profile/vehicles/${id}`);
export const listMyInsurances = () => api.get('/profile/insurances');
export const createInsurance = (data) => api.post('/profile/insurances', data);
export const updateInsurance = (id, data) => api.patch(`/profile/insurances/${id}`, data);
export const deleteInsurance = (id) => api.delete(`/profile/insurances/${id}`);
// Admin Profile/Vehicles/Insurances
export const adminListProfiles = (limit = 200, userType = null) => {
  const params = userType ? `?limit=${limit}&user_type=${userType}` : `?limit=${limit}`;
  return api.get(`/profile/admin/profiles${params}`);
};
export const adminGetUserFull = (userId) => api.get(`/profile/admin/users/${userId}`);
export const adminListAllVehicles = (limit = 500) => api.get(`/profile/admin/vehicles?limit=${limit}`);
export const adminListAllInsurances = (limit = 500) => api.get(`/profile/admin/insurances?limit=${limit}`);

// Uploads (Profilbild + Fahrzeug-Fotos)
export const uploadProfilePicture = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/uploads/profile-picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteProfilePicture = () => api.delete('/uploads/profile-picture');
export const uploadVehiclePhoto = (vehicleId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/uploads/vehicle-photo/${vehicleId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteVehiclePhoto = (vehicleId, photoId) => api.delete(`/uploads/vehicle-photo/${vehicleId}/${photoId}`);

// Service-Offers (Admin-Quote + User-Pay)
export const adminCreateOffer = (data) => api.post('/offers/admin/create', data);
export const adminUpdateOffer = (offerId, data) => api.patch(`/offers/admin/${offerId}`, data);
export const adminListOffers = (params = {}) => api.get('/offers/admin/all', { params });
export const adminCancelOffer = (offerId, reason) => api.post(`/offers/admin/${offerId}/cancel`, { reason });
export const updateMyPhone = (phone) => api.patch('/auth/me/phone', { phone });
export const getOfferPdf = (offerId) => api.get(`/offers/${offerId}/pdf`);
export const extractRatehawk = (inquiryId, url) => api.post(`/hotel-bookings/admin/${inquiryId}/extract`, { ratehawk_url: url });
export const sendHotelOffer = (inquiryId, payload) => api.post(`/hotel-bookings/admin/${inquiryId}/send-offer`, payload);
export const requestHotelPayment = (inquiryId, payload) => api.post(`/hotel-bookings/admin/${inquiryId}/request-payment`, payload);
export const setHotelDiscount = (inquiryId, payload) => api.post(`/hotel-bookings/admin/${inquiryId}/discount`, payload);
export const adminListHotelBookings = (params = {}) => api.get('/hotel-bookings/admin/list', { params });
export const markHotelPaid = (inquiryId) => api.post(`/hotel-bookings/admin/${inquiryId}/mark-paid`);
export const acceptHotelOffer = (offerId, form) => api.post(`/hotel-bookings/offers/${offerId}/accept`, form);
export const getMyNotifications = () => api.get('/notifications/my');
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/read-all');

// ── Service-Fälle (zentrale Kundenakte) ──
export const adminListServiceCases = (params = {}) => api.get('/service-cases/admin/list', { params });
export const adminGetServiceCase = (caseId) => api.get(`/service-cases/admin/${caseId}`);
export const adminPatchServiceCase = (caseId, payload) => api.patch(`/service-cases/admin/${caseId}`, payload);
export const getMyServiceCases = () => api.get('/service-cases/my');
export const getCaseMessages = (caseId) => api.get(`/service-cases/${caseId}/messages`);
export const sendCaseMessage = (caseId, payload) => api.post(`/service-cases/${caseId}/messages`, payload);
export const getMyOffers = () => api.get('/offers/me');
export const getOffer = (offerId) => api.get(`/offers/${offerId}`);
export const payOffer = (offerId, originUrl) => api.post(`/offers/${offerId}/pay`, { origin_url: originUrl });
export const rejectOffer = (offerId) => api.post(`/offers/${offerId}/reject`);
export const getOfferInvoice = (offerId) => api.get(`/offers/${offerId}/invoice`);
export const getOfferCreditNote = (offerId) => api.get(`/offers/${offerId}/credit-note`);

// Wallet — Transaction History
export const getMyWalletTransactions = (params = {}) => api.get('/wallet/transactions', { params });

// Live-Preis-Schätzer
export const getServiceEstimate = (params) => api.get('/calculator/service-estimate', { params });

// Documents (Vehicle/Insurance/Invoice Uploads)
export const uploadDocument = (formData) => api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const scanInvoice = (formData) => api.post('/documents/scan-invoice', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const listMyDocuments = (params = {}) => api.get('/documents/me', { params });
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
export const adminListUserDocuments = (userId, inquiryId) => api.get(`/documents/admin/user/${userId}`, inquiryId ? { params: { inquiry_id: inquiryId } } : undefined);
export const adminListInquiryDocuments = (inquiryId) => api.get(`/documents/admin/inquiry/${inquiryId}`);
export const crmListCustomers = (params = {}) => api.get('/crm/customers', { params });
export const crmGetCustomer = (userId) => api.get(`/crm/customers/${userId}`);
export const crmAddNote = (userId, text) => api.post(`/crm/customers/${userId}/notes`, { text });
export const crmDeleteNote = (userId, noteId) => api.delete(`/crm/customers/${userId}/notes/${noteId}`);
export const crmGdprDelete = (userId) => api.delete(`/crm/customers/${userId}/gdpr`);
export const recordConsent = (consentType, extra = {}) => api.post('/consents', { consent_type: consentType, ...extra });
export const getMyConsents = () => api.get('/consents/me');
export const revokeConsent = (consentType) => api.post('/consents/revoke', { consent_type: consentType });
export const requestAccountDeletion = () => api.post('/consents/deletion-request');
export const adminUploadDocumentForUser = (formData) => api.post('/documents/admin/upload-for-user', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Auto-Assistent (15 Inklusiv-Services)
export const getAssistantOverview = () => api.get('/assistant/overview');
export const createAssistantSubmission = (data) => api.post('/assistant/submissions', data);
export const getMyAssistantSubmissions = (serviceKey) => api.get('/assistant/submissions/my', serviceKey ? { params: { service_key: serviceKey } } : undefined);
export const getAssistantData = (serviceKey) => api.get(`/assistant/data/${serviceKey}`);
export const saveAssistantData = (serviceKey, data) => api.put(`/assistant/data/${serviceKey}`, { data });
export const getWartungsplan = () => api.get('/assistant/wartungsplan');
export const getWertVerlauf = () => api.get('/assistant/wert-verlauf');
export const getSparreport = () => api.get('/assistant/sparreport');
export const analyzeSchadenfoto = (formData) => api.post('/assistant/schadenfoto', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 });
export const getSchadenfotoChecks = () => api.get('/assistant/schadenfoto');
export const adminListAssistantSubmissions = (params = {}) => api.get('/assistant/admin/submissions', { params });
export const adminPatchAssistantSubmission = (id, payload) => api.patch(`/assistant/admin/submissions/${id}`, payload);
export const crmGetAssistant = (userId) => api.get(`/assistant/admin/user/${userId}`);
export const getFuelPrices = () => api.get('/fuel-prices/current');

export default api;
